/**
 * TimeGenie App.js - Main application component and routing configuration
 *
 * This is the root component that handles:
 * - Application routing with protected and public routes
 * - Authentication state initialization and management
 * - Error handling and recovery for cache/session issues
 * - Loading states and connection retry logic
 * - Global theme and analytics integration
 */

import { ChakraProvider, Box, Center, Text, Button, useToast } from '@chakra-ui/react';
import React, { useEffect, useState, lazy, Suspense, useContext } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import ErrorBoundary from './components/ErrorBoundary';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PublicRoute } from './components/PublicRoute';
import SplashScreen from './components/SplashScreen';
// Legacy UIWrapper deprecated in favor of AppLayout
import { supabase } from './config/supabase';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { TimezoneProvider } from './context/TimezoneContext';
// Route-level code splitting: lazy-load route components
const InviteCompletionPage = lazy(() => import('./pages/InviteCompletionPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
import { theme } from './theme/index';
import { cacheManager } from './utils/cacheManager';
import logger from './utils/logger';
import { getAppSessionId, getUuidSessionId } from './utils/activityEvents';
// New UI layout (mobile-first tabs)
const AppLayout = lazy(() => import('./ui-new/AppLayout'));
// Temporarily disable diagnostic logging to reduce console noise
// import { tabReopenDiagnostics, logTabEvent } from './utils/tabReopenDiagnostics';
// Note: Vercel Analytics import removed to avoid unresolved dependency. Re-add when dependency is installed.

/**
 * AppContent - Defines the main application routing structure
 *
 * Sets up all application routes with appropriate protection:
 * - Public routes: login, password reset (accessible without auth)
 * - Protected routes: dashboard, goals, tasks, settings, chat (require auth)
 * - Catch-all route redirects to dashboard
 *
 * @returns {JSX.Element} The application routes wrapped in a container
 */
function AppContent() {
  // Track page views on route changes for authenticated users
  const location = useLocation();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    try {
      const uid = /** @type {any} */ (user)?.id;
      if (!uid) return;
      /** @type {Record<string, any>} */
      const client = {
        platform: typeof navigator !== 'undefined' ? navigator.platform || 'web' : 'web',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        locale:
          (typeof navigator !== 'undefined' && (navigator.language || navigator.languages?.[0])) ||
          'en-US',
        app_session_id: getAppSessionId(),
        app_version:
          process.env.REACT_APP_VERSION || process.env.REACT_APP_APP_VERSION || undefined,
      };
      if (client.app_version === undefined) delete client.app_version;
      /** @type {Record<string, any>} */
      const meta = {
        route:
          typeof window !== 'undefined'
            ? window.location?.pathname
            : String(location.pathname || ''),
        referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
        title: typeof document !== 'undefined' ? document.title || '' : '',
      };
      // Non-blocking insert; ignore failures
      void supabase
        .from('user_activity_events')
        .insert([
          {
            user_id: uid,
            event_type: 'page_view',
            session_id: getUuidSessionId(),
            client,
            meta,
          },
        ])
        .select('id')
        .single();
    } catch (_) {
      // swallow errors; analytics should not impact UX
    }
  }, [location.pathname, user?.id]);

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/invite-completion"
          element={
            <ProtectedRoute>
              <InviteCompletionPage />
            </ProtectedRoute>
          }
        />
        {/* New UI routes (deprecated legacy UIWrapper for main app) */}
        {/* Root should redirect to login for first-load after sign-in */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route
          path="/today/*"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat/*"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/goals/*"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/journey/*"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/*"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendar/*"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/updates/*"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        />
        {/* Redirect legacy routes */}
        <Route path="/plans" element={<Navigate to="/goals" replace />} />
        <Route path="/tasks" element={<Navigate to="/today" replace />} />
        <Route path="/explore" element={<Navigate to="/goals" replace />} />
        {/* Settings rendered inside new layout (tabs hidden automatically) */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SettingsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Remove dev-only ui-new routes; redirect to primary paths */}
        <Route path="/ui-new/chat" element={<Navigate to="/chat" replace />} />
        <Route path="/ui-new/explore" element={<Navigate to="/goals" replace />} />
        <Route path="/ui-new/journey" element={<Navigate to="/journey" replace />} />
        {/* Fallback: send any unknown route to Today */}
        <Route path="*" element={<Navigate to="/today" replace />} />
      </Routes>
    </Suspense>
  );
}

/**
 * AppWithSplash - Wrapper component that shows splash screen during auth loading
 *
 * Shows an animated splash screen while auth is initializing, with a minimum
 * display time to prevent jarring flashes. Once auth is resolved and minimum
 * time has elapsed, renders the main app content.
 *
 * @param {{ error: string|null, retryConnection: () => Promise<void> }} props
 * @returns {JSX.Element}
 */
function AppWithSplash({ error, retryConnection }) {
  const { loading } = useContext(AuthContext);

  return (
    <>
      {/* Splash screen shown during auth loading with minimum display time */}
      <SplashScreen isLoading={loading} />

      {/* Error banner if needed */}
      {error && (
        <Box bg="yellow.100" borderWidth="1px" borderColor="yellow.400" p={3} textAlign="center">
          <Text color="yellow.800" fontWeight="medium">
            Warning: {error}
          </Text>
          <Button colorScheme="yellow" variant="outline" size="xs" ml={2} onClick={retryConnection}>
            Retry
          </Button>
          <Button
            variant="outline"
            colorScheme="gray"
            size="xs"
            ml={2}
            onClick={async () => {
              await cacheManager.forceRefresh();
            }}
          >
            Clear Cache & Reload
          </Button>
        </Box>
      )}

      {/* Main app content */}
      <AppContent />
    </>
  );
}

/**
 * App - Main application component with authentication and error handling
 *
 * Manages:
 * - Session initialization and loading states
 * - Error handling and recovery mechanisms
 * - Cache management and cleanup
 * - Retry logic for connection issues
 * - Global UI state and theme provider
 *
 * @returns {JSX.Element} Root providers and routed application UI
 */
function App() {
  const [_sessionLoaded, setSessionLoaded] = useState(false); // Tracks if auth session has been loaded
  const [error, setError] = useState(/** @type {string|null} */ (null)); // Stores any initialization errors
  const [retryCount, setRetryCount] = useState(0); // Tracks connection retry attempts
  const _toast = useToast(); // Chakra UI toast notifications

  /**
   * Handle cache-related errors and attempt recovery with diagnostics.
   *
   * - Runs cache diagnostics and logs issues
   * - Allows only one auto force-refresh per session to avoid reload loops
   * - Falls back to showing a warning if recovery was already attempted
   *
   * @param {string} errorMessage Description of the error that occurred
   * @returns {Promise<boolean>} True if a force refresh was triggered, otherwise false
   */
  const handleCacheError = async (errorMessage) => {
    logger.warn('🔄 App: Cache error detected:', errorMessage);

    // Use a sessionStorage flag to prevent infinite reload loops
    const RECOVERY_FLAG = 'timegenie_auto_recovery_attempted';
    const alreadyTried = sessionStorage.getItem(RECOVERY_FLAG);

    try {
      // Run diagnostics for logging only
      const diagnostics = await cacheManager.diagnostics();
      logger.info('📊 Cache diagnostics (info only):', diagnostics);

      // Only allow one auto-force-refresh per session
      if (diagnostics.issues.length > 0 && !alreadyTried) {
        sessionStorage.setItem(RECOVERY_FLAG, '1');
        logger.info(
          '🧹 App: Fatal cache/session issue detected. Triggering one-time force refresh...',
        );
        await cacheManager.forceRefresh();
        // window.location.reload(true); // forceRefresh already reloads
        return true;
      }
    } catch (cacheError) {
      // Only allow one auto-force-refresh per session
      if (!alreadyTried) {
        sessionStorage.setItem(RECOVERY_FLAG, '1');
        logger.error('❌ Cache diagnostics failed, attempting one-time force refresh:', cacheError);
        await cacheManager.forceRefresh();
        return true;
      }
      // If already tried, just show error UI
      logger.error('❌ Cache diagnostics failed and auto-recovery already attempted:', cacheError);
    }
    // If already tried, just show error UI and do not reload
    return false;
  };

  /**
   * retryConnection - Attempts to retry failed connections with cache management
   *
   * Implements exponential backoff retry logic:
   * - Tracks retry attempts and limits to 3 maximum
   * - Attempts cache recovery after max retries
   * - Increments retry counter and clears errors on success
   */
  const retryConnection = async () => {
    if (retryCount >= 3) {
      logger.info('🔄 App: Max retries reached, attempting force refresh...');
      await cacheManager.forceRefresh();
      return;
    }

    setRetryCount((prev) => prev + 1);
    setError(null);
    setSessionLoaded(false);

    // Try cache recovery first
    const cacheRecovered = await handleCacheError('Manual retry triggered');
    if (cacheRecovered) {
      return;
    }

    // Note: Session management now handled by AuthContext
    logger.info('🔄 Manual retry completed - relying on AuthContext for session management');
  };

  // Initialize authentication state and set up listeners
  useEffect(() => {
    logger.info(
      '🚀 App: Initializing application (simplified - letting AuthContext handle session)',
    );

    // Check for required environment variables only
    // In development, do NOT block the UI; show a warning banner instead.
    // In production, missing env vars are fatal and must block rendering.
    if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
      const errorMsg = 'Missing required environment variables. Please check your configuration.';
      logger.error('❌', errorMsg);
      setError(errorMsg);
      setSessionLoaded(true);
      if (process.env.NODE_ENV === 'production') {
        // In production builds, stop initialization here to avoid undefined behavior
        return;
      }
      // In development, continue; supabase.js provides safe fallbacks/logging
    }

    // Set up Supabase auth state change listener for UI updates only
    // AuthContext handles the actual session management
    logger.info('🔍 App: Setting up auth state change listener for UI updates...');
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      logger.info('🔍 App: Auth state changed:', { event, session });
      setSessionLoaded(true);
      setError(null); // Clear any previous errors
    });

    // Set session loaded immediately - AuthContext will handle the actual auth logic
    setSessionLoaded(true);

    return () => {
      logger.info('🔍 App: Cleaning up auth listener');
      subscription?.unsubscribe();
    };
  }, []);

  // Only block the entire UI for truly fatal errors (missing environment variables)
  // Other errors are shown as warning banners but don't prevent app usage.
  // Fatal block applies only in production; in development we allow the app to render.
  if (
    process.env.NODE_ENV === 'production' &&
    typeof error === 'string' &&
    error.includes('environment variables')
  ) {
    return (
      <Center h="100vh" flexDirection="column" gap={4} p={4} textAlign="center">
        <Box bg="red.50" p={4} borderRadius="md" borderWidth="1px" borderColor="red.200" maxW="md">
          <Text color="red.800" fontWeight="medium">
            Fatal Configuration Error
          </Text>
          <Text mt={2} color="gray.700" fontSize="sm">
            {error}
          </Text>
        </Box>
      </Center>
    );
  }

  // Main app render - always shows the app with optional warning banner
  // Non-fatal errors are displayed as dismissible warnings at the top
  return (
    <ChakraProvider theme={theme}>
      <AuthProvider>
        <TimezoneProvider>
          <ErrorBoundary>
            <AppWithSplash error={error} retryConnection={retryConnection} />
          </ErrorBoundary>
        </TimezoneProvider>
      </AuthProvider>
    </ChakraProvider>
  );
}

export default App;
